/**
 * The message box that stays on screen (H12).
 *
 * Chat was behind a button, which is the right shape for something you use
 * occasionally and the wrong one for talking. A table where somebody has to
 * open a panel to say "nice hand" is a table where nobody says it.
 *
 * ## When it is here
 *
 * Three states, and they follow from what else is available rather than from a
 * preference:
 *
 * - **Typing allowed, not in voice** - the bar is here. This is most tables.
 * - **In voice** - it goes. You are already talking; a text box under a live
 *   conversation is a second channel nobody is reading, and it is 56px that
 *   the table wants back. Chat is still reachable from the menu.
 * - **Typing off** - no bar at all, because there is nothing to type into. The
 *   quick phrases remain, in the menu and in the action row.
 *
 * Note that it is joining voice that hides it, not voice being *possible*.
 * Otherwise every table with voice enabled would lose its chat bar to a
 * conversation nobody had started.
 */

import { useState } from 'react';
import { Limits, normaliseChatText } from '@cards/protocol';
import { Icon } from '../render/Icon.js';

export function ChatBar({
  onType,
  onPhrases,
}: {
  onType: (text: string) => void;
  /** Opens the full sheet, where the quick phrases and the log live. */
  onPhrases: () => void;
}) {
  const [draft, setDraft] = useState('');

  /*
   * Tidied the same way the server will tidy it, so the two agree about
   * whether this fits - a client measuring what was typed rather than what
   * will be sent would disagree with the server at the boundary.
   */
  const ready = normaliseChatText(draft);
  const canSend = ready.length > 0 && ready.length <= Limits.CHAT_TEXT;

  return (
    <form
      className="chat-bar"
      onSubmit={(event) => {
        event.preventDefault();
        if (!canSend) return;
        onType(ready);
        setDraft('');
      }}
    >
      {/*
        The way to the phrases and the log.

        A face rather than a plus: what is behind it is mostly the twelve
        things you can say without typing, and those are the closest thing
        this app has to a reaction.
      */}
      <button
        type="button"
        className="chat-bar-phrases"
        onClick={onPhrases}
        aria-label="Quick phrases and what has been said"
      >
        <Icon name="message" />
      </button>

      <input
        className="chat-bar-input"
        type="text"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        placeholder="Type a message…"
        aria-label="Type a message"
        // Generous, because the real limit applies to the tidied text.
        maxLength={Limits.CHAT_TEXT * 2}
        autoComplete="off"
      />

      <button type="submit" className="chat-bar-send" disabled={!canSend} aria-label="Send">
        <Icon name="play" />
      </button>
    </form>
  );
}
